// ── Data Quality Scoring Utility ─────────────────────────────────────────────
// Shared across Clients list, Cases list, ClientDetail, CaseDetail, Analytics

// Score a client record — returns { score, total, missing, pct, colour, label }
export function scoreClient(client) {
  if (!client) return null;

  const checks = [
    // Personal (weight: important)
    { field: 'first_name', label: 'First name', value: client.first_name },
    { field: 'last_name', label: 'Last name', value: client.last_name },
    { field: 'dob', label: 'Date of birth', value: client.dob },
    { field: 'phone', label: 'Phone number', value: client.phone },
    { field: 'email', label: 'Email address', value: client.email },
    { field: 'current_address', label: 'Current address', value: client.current_address },
    { field: 'postcode', label: 'Postcode', value: client.postcode },
    // Financial
    { field: 'income', label: 'Annual income', value: client.income },
    { field: 'employment_type', label: 'Employment type', value: client.employment_type },
    // Admin
    { field: 'lead_source', label: 'Lead source', value: client.lead_source },
  ];

  const scored = checks.map(c => ({
    ...c,
    present: c.value !== null && c.value !== undefined && c.value !== '' && c.value !== false,
  }));

  const score = scored.filter(c => c.present).length;
  const total = checks.length;
  const pct = Math.round((score / total) * 100);
  const missing = scored.filter(c => !c.present).map(c => c.label);

  return { score, total, pct, missing, ...getQualityMeta(pct) };
}

// Score a case record
export function scoreCase(c) {
  if (!c) return null;

  const isMortgage = c.product_type === 'mortgage';
  const isInsurance = c.product_type === 'insurance';

  const checks = [
    // Core — always required
    { label: 'Product type', value: c.product_type },
    { label: 'Status', value: c.status },
    { label: 'Mortgage type', value: isMortgage ? c.mortgage_type : true },
    { label: 'Insurance type', value: isInsurance ? c.insurance_type : true },
    // Mortgage details
    { label: 'Lender name', value: isMortgage ? c.lender_name : true },
    { label: 'Interest rate', value: isMortgage ? c.interest_rate : true },
    { label: 'Term (years)', value: c.term_years },
    { label: 'Repayment type', value: isMortgage ? c.repayment_type : true },
    { label: 'Property type', value: isMortgage ? c.property_type : true },
    { label: 'Security address', value: isMortgage ? c.security_address : true },
    // Dates
    { label: 'Application date', value: c.date_application_submitted },
    { label: 'Product expiry date', value: c.product_expiry_date },
    // Commission
    { label: 'Proc fee total', value: c.proc_fee_total },
    { label: 'Commission %', value: c.commission_percentage },
    // Insurance fields
    { label: 'Insurance provider', value: isInsurance ? c.insurance_provider : true },
    { label: 'Monthly premium', value: isInsurance ? c.monthly_premium : true },
    { label: 'Sum assured', value: isInsurance ? c.sum_assured : true },
  ];

  const scored = checks.map(ch => ({
    ...ch,
    present: ch.value !== null && ch.value !== undefined && ch.value !== '' && ch.value !== false && ch.value !== true ? true : ch.value === true ? true : false,
  }));

  // Fix: true means "not applicable for this product type" = counts as complete
  const fixedScored = checks.map(ch => {
    const notApplicable = ch.value === true;
    const hasValue = ch.value !== null && ch.value !== undefined && ch.value !== '' && ch.value !== false && ch.value !== true;
    return { ...ch, present: notApplicable || hasValue };
  });

  const score = fixedScored.filter(c => c.present).length;
  const total = checks.length;
  const pct = Math.round((score / total) * 100);
  const missing = fixedScored.filter(c => !c.present).map(c => c.label);

  return { score, total, pct, missing, ...getQualityMeta(pct) };
}

// Shared colour/label logic
function getQualityMeta(pct) {
  if (pct >= 90) return {
    colour: 'green',
    label: 'Complete',
    badgeCls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    barCls: 'bg-green-500',
    ringCls: 'text-green-600 dark:text-green-400',
  };
  if (pct >= 70) return {
    colour: 'amber',
    label: 'Mostly complete',
    badgeCls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    barCls: 'bg-yellow-400',
    ringCls: 'text-yellow-600 dark:text-yellow-400',
  };
  return {
    colour: 'red',
    label: 'Needs attention',
    badgeCls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    barCls: 'bg-red-500',
    ringCls: 'text-red-600 dark:text-red-400',
  };
}

// Mini badge for list views
export function QualityBadge({ record, type }) {
  const result = type === 'client' ? scoreClient(record) : scoreCase(record);
  if (!result) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${result.badgeCls}`}>
      {result.pct}%
    </span>
  );
}

// Full quality panel for detail pages
export function QualityPanel({ record, type }) {
  const result = type === 'client' ? scoreClient(record) : scoreCase(record);
  if (!result) return null;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-900 dark:text-white">Data Quality</span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${result.badgeCls}`}>
            {result.label}
          </span>
        </div>
        <span className={`text-xl font-bold ${result.ringCls}`}>{result.pct}%</span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${result.barCls}`}
          style={{ width: `${result.pct}%` }}
        />
      </div>

      {/* Score */}
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {result.score} of {result.total} fields complete
      </p>

      {/* Missing fields */}
      {result.missing.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-2">Missing fields:</p>
          <div className="flex flex-wrap gap-1.5">
            {result.missing.map(f => (
              <span
                key={f}
                className="text-xs px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600"
              >
                {f}
              </span>
            ))}
          </div>
        </div>
      )}

      {result.missing.length === 0 && (
        <p className="text-xs text-green-600 dark:text-green-400 font-medium">✓ All fields complete</p>
      )}
    </div>
  );
}
